import _ from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Find the nearest scrollable ancestor of an element
 */
export function getScrollableAncestor(element) {
  let parent = element?.parentElement;
  while (parent) {
    const style = getComputedStyle(parent);
    const overflowY = style.overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
}

/**
 * Hook to track if an element is visible within its scrollable ancestor
 */
export function useScrollVisibility(referenceElement, isOpen) {
  const [isVisible, setIsVisible] = useState(true);

  const checkVisibility = useCallback(() => {
    if (!referenceElement) {
      setIsVisible(true);
      return;
    }

    const rect = referenceElement.getBoundingClientRect();
    const container = getScrollableAncestor(referenceElement);

    if (!container) {
      const visible =
        rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth;
      setIsVisible(visible);
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const visible =
      rect.bottom > containerRect.top &&
      rect.top < containerRect.bottom &&
      rect.right > containerRect.left &&
      rect.left < containerRect.right;

    setIsVisible(visible);
  }, [referenceElement]);

  // Throttle scroll handler to avoid performance issues
  const checkVisibilityRef = useRef(checkVisibility);
  checkVisibilityRef.current = checkVisibility;

  const throttledCheck = useMemo(
    () => _.throttle(() => checkVisibilityRef.current(), 100),
    [], // No dependencies - stable throttle instance
  );

  useEffect(() => {
    if (!isOpen) {
      setIsVisible(true);
      return;
    }

    checkVisibility();

    window.addEventListener('scroll', throttledCheck, true);
    window.addEventListener('resize', throttledCheck);

    return () => {
      throttledCheck.cancel();
      window.removeEventListener('scroll', throttledCheck, true);
      window.removeEventListener('resize', throttledCheck);
    };
  }, [isOpen, checkVisibility, throttledCheck]);

  return isVisible;
}
